import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector, DefaultRootState } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import usePagination from '../../hooks/usePagination';
import {
    fetchSlotfilterList,
    GetDeliveryBDOrgs,
    GetDeliveryHubs,
} from '../../store/actions/settingsActionCreator';
import { preProcessPayload } from './hub.helper';
import {
    Box,
    Button,
    IconButton,
    ListItemSecondaryAction,
    Typography,
    withStyles,
} from '@material-ui/core';
import useRiderOnboardingStyles from './useHubManagementStyles';
import clsx from 'clsx';
import { SLOT_TYPE_CLASS_TYPE_MAPPING } from '../settings/slot-listing/slot.helper';
import {
    FormatTime,
    formatCurrencyV1,
    currencyFormatWithoutDecimal,
} from '../../utils/appHelper';
import { convertRStoAmount } from '../cluster-management/helper.cm';
import useCommonStyles from '../../components/UI/CommonStyles';
import { getActiveInitRoute, getPermissions } from '../../utils/permissions';
import EditIcon from '@material-ui/icons/Edit';
import { ActionIcon } from './utility.component.hub';
import ShiftColumnItemPopper from '../../components/Containers/ShiftColumnItemPopper';
import ShiftColumnItemPopperV2 from '../../components/Containers/ShiftColumnItemPopperV2';

const useHubManagement = (props: any) => {
    const containerRef = useRef<HTMLElement>(null);
    const permissions = getPermissions('ops_management');
    const dispatch = useDispatch();
    const history = useHistory();
    const initialPage = 0; // Start on page 0 (0-based index)
    const initialPageSize = 25; // Show 25 rows per page
    const initialActiveFilterCard = {
        type: 'Total',
        subType: '',
    }; // Example filter card
    const initialFilterValues = {}; // Example filter values
    const initialSearchText = ''; // Example search text
    const initialPayload = {
        pageNo: initialPage,
        pageSize: initialPageSize,
        searchText: '',
        filters: {
            ...initialFilterValues,
            ...props?.externalFiltered,
        },
        ...props?.externalFiltered,
    };
    // `disableAutoSearch` is the fix for the duplicated list calls: without it,
    // usePagination turns every `searchTextValue` change into a fresh payload,
    // and Searchbar pushed a change on each keystroke *and* through its debounce
    // — so typing fired one request per character. It also stops the post-mount
    // `payload` rewrite, making the initial load single-fire by construction
    // rather than by the signature guard below.
    const disableAutoSearch = true;
    const {
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        activeFilterCard,
        filterValues,
        searchTextValue,
        payload,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        setFilterValues,
        clearSearch,
        onManualSearch,
        onManualSearchClear,
    } = usePagination(
        initialPage,
        initialPageSize,
        initialActiveFilterCard,
        initialFilterValues,
        initialSearchText,
        initialPayload,
        disableAutoSearch,
    );
    const [loading, setLoading] = useState(false);
    const [selectedHub, setSelectedHub] = useState(null);
    const [isOpen, toggleModal] = useState(false);
    const [openBulkModal, setBulkModalOpen] = useState(false);
    const [bulkUploadType, setBulkUploadType] = useState(null);
    const [containerHeight, setContainerHeight] = useState(0);

    const { user, mapview, staff, settings } = useSelector(
        (store: DefaultRootState | any) => store || {},
    );
    const hubList: any = settings?.hubs;
    const styles: any = useRiderOnboardingStyles({
        containerHeight: containerHeight,
    });
    const commonStyles: any = useCommonStyles();

    const dispatchGetHubs = useCallback(
        (data: any, onSuccess: Function, onError: Function) => {
            dispatch(GetDeliveryBDOrgs(data, onSuccess, onError));
        },
        [dispatch],
    );
    const currentOrgId = settings?.basicDetails?.id || user?.orgId;

    // There is no request cancellation in the API middleware, so tag every
    // request and let only the latest one settle the loading flag — otherwise a
    // slow earlier response clears the spinner while a newer one is in flight.
    const requestIdRef = useRef(0);

    const fetchHubsData = useCallback(() => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        const preProceedPayload = preProcessPayload(payload);
        // UI tracks pages 0-based; the backend expects 1-based page numbers,
        // so bump pageNo by one right before dispatching.
        const requestPayload = {
            ...preProceedPayload,
            pageNo: (preProceedPayload?.pageNo ?? 0) + 1,
        };
        const settle = () => {
            if (requestIdRef.current === requestId) {
                setLoading(false);
            }
        };
        dispatchGetHubs(requestPayload, settle, settle);
    }, [payload, dispatchGetHubs]);
    //hooks

    // Second line of defence behind `disableAutoSearch`: only fetch when the
    // payload's *content* changed, so any future re-render that hands back an
    // equivalent payload object can't re-trigger the list call.
    const lastFetchSignatureRef = useRef<string | null>(null);

    useEffect(() => {
        const signature = JSON.stringify(payload);
        if (lastFetchSignatureRef.current === signature) return;
        lastFetchSignatureRef.current = signature;
        fetchHubsData();
    }, [payload, fetchHubsData]);

    // Rows may have changed while a modal was open, so bypass the signature
    // guard and refetch the current page.
    const refetchHubsData = useCallback(() => {
        lastFetchSignatureRef.current = null;
        fetchHubsData();
    }, [fetchHubsData]);

    const appliedSearchText = payload?.searchText ?? '';
    const isSearchDirty =
        (searchTextValue ?? '').trim() !== (appliedSearchText ?? '').trim();

    const handleSearchSubmit = () => {
        onManualSearch(searchTextValue);
    };

    const handleSearchClear = () => {
        clearSearch();
        onManualSearchClear();
    };

    useEffect(() => {
        // Perform side effects or data fetching here
        return () => {
            // Cleanup if necessary
        };
    }, []);

    const prepareFilterCard = (card: any) => {
        switch (card?.type) {
            default:
                return (
                    <>
                        <Typography className={styles.cardHeader}>
                            {card?.name}
                        </Typography>
                        <Typography className={styles.cardSubHeader}>
                            {hubList?.[card?.valueMappingKey] ?? '0'}
                        </Typography>
                    </>
                );
        }
    };
    useEffect(() => {
        if (!!containerRef?.current?.offsetHeight && !containerHeight) {
            const height = containerRef.current.offsetHeight;
            setContainerHeight(height);
        }
    }, [containerRef?.current?.offsetHeight, containerHeight]);

    // The store's hubs live on their own page rather than an inline expansion: a store can hold
    // several and the page carries a search bar, which a row-expansion has nowhere to put.
    const handleRowClick = (e: any, val: any) => {
        // A click on a control inside the row is that control's, not the row's. The shift
        // add/edit/delete buttons live in a shared popper component and do not stop propagation —
        // they had no reason to while this handler was a no-op, and patching them would reach
        // every other page that renders it. Checking the origin here covers those and anything
        // added to a row later.
        if (
            typeof e?.target?.closest === 'function' &&
            e.target.closest(
                'button, a, input, select, textarea, [role="button"]',
            )
        ) {
            return;
        }
        const storeId = val?.bluedartUniqueStoreId;
        if (!storeId) {
            return;
        }
        history.push(`/ops-management/store/${encodeURIComponent(storeId)}`);
    };

    const getColumnComponentValue = (
        row: any,
        column: any,
        handleAction: any,
    ) => {
        let compValue: any = (
            <Box>
                <Typography className={styles.tableValue}>
                    {row?.[column?.mappingKey] ?? '-/-'}
                </Typography>
            </Box>
        );
        const orderStatus: any = row?.slotType
            ?.toUpperCase()
            ?.replace(' ', '_')
            ?.trim();

        let chipOrderClass: any =
            styles?.[`${SLOT_TYPE_CLASS_TYPE_MAPPING?.[orderStatus]}`];
        switch (column?.mappingKey) {
            case 'date':
                compValue = (
                    <Box>
                        <Typography className={styles.tableValue}>
                            {row?.displayDateMainText ?? '-'}
                        </Typography>
                        <Typography className={styles.tableSubValue}>
                            {row?.displayDateSubText ?? '-'}
                        </Typography>
                    </Box>
                );
                break;
            case 'segment':
                compValue = (
                    <Box>
                        <Typography className={styles.tableValue}>
                            {row?.segment ?? '-'}
                        </Typography>
                    </Box>
                );
                break;
            case 'startTime':
                compValue = (
                    <Box>
                        <Typography className={clsx(styles.tableValue)}>
                            {FormatTime(row?.startTime ?? '')}
                        </Typography>
                    </Box>
                );
                break;
            case 'endTime':
                compValue = (
                    <Box>
                        <Typography className={clsx(styles.tableValue)}>
                            {FormatTime(row?.endTime ?? '')}
                        </Typography>
                    </Box>
                );
                break;
            case 'shiftCount':
                compValue = (
                    <Box
                        // Everything in this cell belongs to the shift controls, not to the row.
                        // Stopped here rather than per control: Edit and Delete are IconButtons
                        // but Add is a plain Box, so there is no single element type the row
                        // handler could test for that covers all three.
                        onClick={(event: any) => event.stopPropagation()}
                        style={{
                            justifySelf: 'flex-start',
                        }}
                    >
                        {!row?.dedicatedSlotBookingEnabled && (
                            <ShiftColumnItemPopperV2
                                limit={1}
                                data={{
                                    hubShiftMappings: [row],
                                    totalHubCount: 1,
                                    totalShiftCount: row?.shiftCount,
                                }}
                                hubInfo={{
                                    ...row,
                                }}
                                // Shift create and edit follow the grant, per the permission
                                // matrix: Super Admin, Regional Approver and Rider Approver may do
                                // both; Rider Viewer may do neither. update/delete were previously
                                // hardcoded here, which hid Edit from everyone and showed Delete to
                                // everyone — including a read-only Rider Viewer.
                                permissions={getPermissions('ops_management')}
                                onEditSuccess={() => {
                                    // Refresh shift list after edit
                                    fetchHubsData();
                                }}
                                onDeleteSuccess={() => {
                                    // Refresh shift list after delete
                                    fetchHubsData();
                                }}
                                onAddSuccess={() => {
                                    // Refresh shift list after add
                                    fetchHubsData();
                                }}
                            />
                        )}
                    </Box>
                );
                break;

            case 'staffCount':
                compValue = row?.staffCount ? (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'flex-start'}
                        justifyContent={'flex-start'}
                    >
                        <Typography className={clsx(styles.tableValue)}>
                            <span
                                style={{
                                    color: 'rgb(16, 185, 129)',
                                    fontWeight: 700,
                                }}
                            >
                                {`${row?.activeStaffCount ?? 0}`}
                                {`/${row?.staffCount ?? 0}`}
                            </span>
                        </Typography>
                    </Box>
                ) : (
                    '-'
                );
                break;

            case 'action':
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'flex-end'}
                    >
                        {/* {!!permissions?.update && (
                            <ActionIcon
                                style={{
                                    marginRight: 8,
                                    borderRadius: 8,
                                    padding: 6,
                                }}
                                color="primary"
                                onClick={(event: any) => {
                                    event.stopPropagation();
                                    openHubModal(row);
                                }}
                            >
                                <EditIcon fontSize="small" />
                                <Typography style={{ marginLeft: '8px' }}>
                                    Edit
                                </Typography>
                            </ActionIcon>
                        )} */}
                        {/* <SlotIcon
                            style={{
                                marginRight: 8,
                                borderRadius: 8,
                                padding: 6,
                            }}
                            color="error"
                            isDelete={true}
                            onClick={(event: any) => {
                                event?.stopPropagation();
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </SlotIcon> */}
                    </Box>
                );
                break;

            default:
                break;
        }
        return compValue;
    };

    const handleSingleRidersPage = (rider: any) => {};

    const prepareFilterOptions = () => {
        return [];
    };

    const openHubModal = (hub?: any) => {
        setSelectedHub(hub);
        toggleModal(true);
    };
    const closeHubModal = () => {
        toggleModal(false);
        refetchHubsData();
        setSelectedHub(null);
    };

    const openBulkUploadModal = (type?: any) => {
        setBulkUploadType(type);
        setBulkModalOpen(true);
    };
    const closeBulkUploadModal = () => {
        setBulkModalOpen(false);
        refetchHubsData();
        setBulkUploadType(null);
    };

    return {
        handleRowClick,
        handleSingleRidersPage,
        prepareFilterOptions,
        getColumnComponentValue,
        prepareFilterCard,
        hubList,
        pagination,
        handleChangeRowsPerPage,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
        activeFilterCard,
        filterValues,
        setFilterValues,
        searchTextValue,
        permissions: permissions,
        loading,
        containerRef,
        styles,
        handleChangePage,
        openHubModal,
        closeHubModal,
        selectedHub,
        isOpen,
        payload,
        fetchHubsData,
        refetchHubsData,
        openBulkUploadModal,
        closeBulkUploadModal,
        bulkUploadType,
        openBulkModal,
        handleSearchSubmit,
        handleSearchClear,
        isSearchDirty,
        appliedSearchText,
    };
};

export default useHubManagement;
